import React, { useState } from "react";
import {
  Badge,
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
  Typography,
  Box,
  Button,
  useTheme,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";

const NotificationDropdown = ({ unreadCount = 0, notifications = [] }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const safeNotifications = Array.isArray(notifications) ? notifications : [];
  const API_URL = process.env.REACT_APP_API_URL;
  const routes = {
    "Prayer": "public-prayers",
    "Testimony": "testimonies",
    "User Register": "users",
    "Organization Register": "organizations",
    "Rider": "drivers",
    "Generosity Giver Item": "giver-items",
    "Generosity New Item Request": "new-item-requests"
  };

  const handleNotification = async (id, module_id, title) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}readnotification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: id,
          module_id: module_id,
          title: title
        }),
      });
      const data = await res.json();

      if (!res.ok || data.success === false) {
        if (data.message === 'Invalid token') {
          localStorage.removeItem('token');
          window.location.href = '/login';
          return;
        }
      }

      // Close the dropdown
      handleClose();

      // Navigate to the appropriate page
      const route = routes[title];
      if (route) {
        navigate(route);
      }

    } catch (err) {
      console.error("Network error", err);
    }
  };

  const handleSeeMore = () => {
    handleClose();
    navigate('/admin/notifications');
  };


  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          color: isLight ? "#1E293B" : "#FFFFFF",
          bgcolor: isLight ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.08)",
          border: isLight ? "1px solid rgba(0, 0, 0, 0.12)" : "1px solid rgba(255, 255, 255, 0.15)",
          borderRadius: "10px",
          p: 1,
          mx: 0.5,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            color: isLight ? '#0D9488' : '#4CA1AF',
            bgcolor: isLight ? 'rgba(13, 148, 136, 0.12)' : 'rgba(76, 161, 175, 0.2)',
            borderColor: isLight ? '#0D9488' : '#4CA1AF',
            transform: 'scale(1.05)',
          }
        }}
        data-testid="notify-button"
        aria-label="notifications"
      >
        <Badge
          badgeContent={unreadCount || 0}
          invisible={!unreadCount || unreadCount === 0}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              color: '#FFFFFF',
              fontWeight: 800,
              fontSize: '0.72rem',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.5)',
              border: isLight ? '2px solid #FFFFFF' : '2px solid #0F172A',
            }
          }}
          data-testid="notify-badge"
        >
          <NotificationsIcon sx={{ cursor: "pointer", fontSize: 20 }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 320,
            maxHeight: 400,
            borderRadius: 2,
            background: isLight ? '#FFFFFF' : 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(10px)',
            border: isLight ? '1px solid rgba(0, 0, 0, 0.08)' : '1px solid rgba(255,255,255,0.1)',
            boxShadow: isLight ? '0 10px 30px rgba(0,0,0,0.08)' : '0 10px 40px rgba(0,0,0,0.5)',
            color: isLight ? '#0F172A' : '#FFFFFF',
          },
        }}
      >
        <Box px={2} py={1.5} borderBottom={isLight ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(255,255,255,0.1)'}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
            Notifications
          </Typography>
        </Box>
        <Divider sx={{ borderColor: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.1)' }} />

        {safeNotifications.length > 0 ? (
          safeNotifications.map((n) => (
            <MenuItem
              key={n.id}
              onClick={() => {
                handleNotification(n.id, n.module_id, n.title)
              }}
              sx={{
                whiteSpace: "normal",
                alignItems: "flex-start",
                borderBottom: isLight ? '1px solid rgba(0,0,0,0.04)' : '1px solid rgba(255,255,255,0.05)',
                '&:hover': {
                  background: isLight ? 'rgba(43, 130, 144, 0.08)' : 'rgba(76, 161, 175, 0.1)',
                }
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight="bold" sx={{ color: isLight ? '#0F172A' : '#FFFFFF' }}>
                  {n.icon && <i className={n.icon} style={{ marginRight: 8 }}></i>}
                  {n.title}
                </Typography>
                <ListItemText
                  primary={n.message}
                  secondary={n.timestamp}
                  primaryTypographyProps={{ fontSize: "0.85rem", color: isLight ? '#334155' : 'rgba(255,255,255,0.8)' }}
                  secondaryTypographyProps={{ fontSize: "0.75rem", color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)' }}
                />
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" sx={{ color: isLight ? '#94A3B8' : 'rgba(255,255,255,0.5)' }}>
              No notifications
            </Typography>
          </MenuItem>
        )}

        {/* See More Button */}
        {safeNotifications.length > 0 && (
          <>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Button
                onClick={handleSeeMore}
                fullWidth
                sx={{
                  color: '#4CA1AF',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  py: 1,
                  borderRadius: 1.5,
                  background: 'rgba(76, 161, 175, 0.1)',
                  border: '1px solid rgba(76, 161, 175, 0.3)',
                  '&:hover': {
                    background: 'rgba(76, 161, 175, 0.2)',
                    borderColor: 'rgba(76, 161, 175, 0.5)',
                  }
                }}
              >
                See More
              </Button>
            </Box>
          </>
        )}
      </Menu>
    </>
  );
};

export default NotificationDropdown;
